import {
  KeyboardEvent,
  FocusEvent,
  cloneElement,
  HTMLProps,
  forwardRef,
  ReactElement,
  isValidElement,
  useMemo,
  Fragment,
  useLayoutEffect,
  Ref,
} from "react";
import cx from "classnames";

import { childrenToString } from "../../internal/functions";
import { DataTrackingId } from "../../types";
import { useMergeRefs, useTrackingId } from "../../hooks";

import { isNonInteractiveElement } from "./internal/isNonInteractiveTrigger";
import { useTooltipContext } from "./internal/useTooltipContext";

import styles from "./Tooltip.module.scss";

/**
 * Props for the TooltipTrigger component
 * @extends HTMLProps<HTMLElement>
 */
export type TooltipTriggerProps = HTMLProps<HTMLElement> & {
  /**
   * The element that triggers the tooltip
   */
  children: ReactElement;
} & DataTrackingId;

const TooltipTriggerElement = forwardRef<HTMLElement, TooltipTriggerProps>(
  function TooltipTrigger(props, propRef) {
    const { children, className, onKeyDown, onFocus, onBlur, ...rest } = props;
    const context = useTooltipContext();

    // verify if the child can accept a ref
    const childCanAcceptRef = useMemo(
      () =>
        isValidElement(children) &&
        children.type !== Fragment &&
        typeof children.type !== "function",
      [children],
    );

    const refs = useMergeRefs([
      (children as unknown as { ref: Ref<HTMLElement> })?.ref,
      context?.invoker.ref,
    ]);

    // either pass to cloned children, or a span if child doesn't accept a ref
    const childProps = useMemo(() => {
      const baseProps = {
        ref: refs,
        "aria-haspopup": context?.invoker["aria-haspopup"],
        "aria-details": context?.invoker["aria-details"],
        "data-state": context?.invoker["data-state"],
        "aria-describedby": context?.invoker["aria-describedby"],
      };

      if (isNonInteractiveElement(children)) {
        if (!("tabIndex" in (children.props || {}))) {
          (baseProps as HTMLProps<HTMLElement>).tabIndex = 0;
        }
        if (!("role" in (children.props || {}))) {
          (baseProps as HTMLProps<HTMLElement>).role = "button";
        }
      }

      return baseProps;
    }, [context?.invoker, refs, children]);

    const child = childCanAcceptRef ? (
      cloneElement(children as ReactElement, childProps)
    ) : (
      <span {...childProps}>{children}</span>
    );

    const onKeyDownHandler = (e: KeyboardEvent<HTMLElement>) => {
      onKeyDown?.(e);
      context?.invoker.onKeyDown(e);
    };

    const onFocusHandler = (e: FocusEvent<HTMLElement>) => {
      onFocus?.(e);
      context?.invoker.onFocus();
    };

    const onBlurHandler = (e: FocusEvent<HTMLElement>) => {
      onBlur?.(e);
      context?.invoker.onBlur();
    };

    useLayoutEffect(() => {
      const onMouseEnterHandler = () => {
        context?.invoker.onMouseEnter();
      };
      const onMouseLeaveHandler = () => {
        context?.invoker.onMouseLeave();
      };
      context?.invoker.ref.current?.addEventListener(
        "mouseleave",
        onMouseLeaveHandler,
      );
      context?.invoker.ref.current?.addEventListener(
        "mouseenter",
        onMouseEnterHandler,
      );
      return () => {
        context?.invoker.ref.current?.removeEventListener(
          "mouseleave",
          onMouseLeaveHandler,
        );
        context?.invoker.ref.current?.removeEventListener(
          "mouseenter",
          onMouseEnterHandler,
        );
      };
    }, [context?.invoker]);

    const data = {
      children: childrenToString(props.children),
    };

    const trackingId = useTrackingId({
      name: "TooltipTrigger",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    return (
      <span
        className={cx([styles["trigger"]], className)}
        data-anv="tooltip-trigger"
        data-tracking-id={trackingId}
        ref={propRef}
        onKeyDown={onKeyDownHandler}
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
        role="presentation"
        {...rest}
      >
        {child}
      </span>
    );
  },
);

/**
 * TooltipTrigger component that wraps the element that triggers the tooltip.
 *
 * Features:
 * - Wraps any React element to make it a tooltip trigger
 * - Handles mouse and keyboard interactions
 * - Provides proper ARIA attributes for accessibility
 * - Supports all standard HTML element props
 * - Automatically clones child elements with proper refs
 * - Handles both focusable and non-focusable children
 * - Manages mouse enter/leave events for hover behavior
 *
 * @example
 * <Tooltip.Trigger>
 *   <Button>Click me</Button>
 * </Tooltip.Trigger>
 */
export const TooltipTrigger = forwardRef<HTMLElement, TooltipTriggerProps>(
  function TooltipTrigger(props, propRef) {
    return <TooltipTriggerElement {...props} ref={propRef} />;
  },
);
